// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useMemo, useState, useRef } from 'react';
import { node } from 'prop-types';
import useSWR, { useSWRConfig } from 'swr';
import noop from 'lodash/noop';
import { dequal } from 'dequal';
import { createContext } from 'use-context-selector';

import { API_CART } from 'Constants';
import useUser from 'Hooks/useUser';
import useFetch from 'Hooks/useFetch';

export const CartContext = createContext({
  data: null,
  isLoading: false,
  error: undefined,
  refresh: noop,
  hasProduct: noop,
  removeProduct: noop,
  updateProduct: noop,
  updateProducts: noop,
  emptyCart: noop
});

const CartProvider = memo(({ children }) => {
  const { data: user, getUserData, setUserData } = useUser();
  const { cache } = useSWRConfig();
  const fetcher = useFetch();
  const initialCart = cache.get('cart');
  const mergedCart = {
    ...(getUserData('cart') || {}),
    ...(initialCart || {})
  };
  const [cart, setCart] = useState(mergedCart);
  const [isLoading, setLoading] = useState(
    user?.email
    && user?.loggedIn
    && !cart?.items
  );

  const userRef = useRef(user);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const getCount = elements => {
    let totalItems = 0;
    let totalOrderLines = 0;

    const countItems = items => {
      items.forEach(item => {
        totalOrderLines += 1;

        totalItems += item.quantity || item.minQuantity || 1;

        if (item.promoItems) {
          Object.values(item.promoItems).forEach(promoItemsArray => {
            countItems(promoItemsArray);
          });
        }
      });
    };

    countItems(elements);

    return {
      orderLines: totalOrderLines,
      totalItems,
    };
  };

  const response = useSWR(
    (user?.loggedIn && user?.email) ? `${ API_CART }?uid=${ user.email }` : null,
    async path => {
      setLoading(true);
      const data = await fetcher(path);

      if (data) {
        setLoading(false);
      }

      return data;
    },
    {
      refreshInterval: 1000 * 60 * 15,
      revalidateOnMount: true
    }
  );

  useEffect(() => {
    if (
      response?.data?.items
      && !dequal(response.data, cart)
    ) {
      const { totalItems, orderLines } = getCount(response.data.items);
      setCart({
        ...response.data,
        totalItems,
        orderLines,
      });
    }
  }, [response?.data]);

  useEffect(() => {
    if (
      user?.email
      && !cart?.items
    ) {
      response.mutate();
      const localCart = getUserData('cart');
      if (localCart) {
        setCart(localCart);
      }
    }
  }, [user?.email]);

  useEffect(() => {
    if (
      user?.loggedIn
      && response?.data?.items
    ) {
      response.mutate();
    }
  }, [
    user?.loggedIn,
    user?.defaultCurrency,
    user?.withVat
  ]);

  useEffect(() => {
    if (
      cart
      && !dequal(user?.cart, cart)
    ) {
      setUserData({ cart });
    }
  }, [cart]);

  const handleProducts = async data => {
    setLoading(true);
    try {
      const r = await fetcher(API_CART, {
        method: 'POST',
        noCancel: true,
        body: JSON.stringify({
          items: data
          // items: (data?.items || []).filter(prod => prod.sendToServer).map(prod => ({
          //   productId: prod.id || prod.productId,
          //   quantity: prod.quantity
          // }))
        })
      });
      if (r) {
        setLoading(false);
        response.mutate(r, false);
      }
      return r;
    } finally {
      setLoading(false);
    }
  };

  const emptyCart = async () => {
    try {
      const r = await fetcher(API_CART, {
        method: 'POST',
        forceCancel: true,
        body: JSON.stringify({
          items: (cart?.items || []).map(p => {
            const payload = {
              productId: p.id || p.productId,
              quantity: 0,
              minQuantity: p.minQuantity,
              ...(!p.hasPrice && {
                supplierId: p.supplierId,
                supplierOfferId: p.supplierOfferId,
              })
            };
            if (p.promotion) {
              payload.promotionId = p.promotion.promotionId;
            }
            return payload;
          })
        })
      });
      if (r) {
        response.mutate(r, false);
      }
    } catch (err) {
      // revert
      response.mutate({
        ...cart,
        messages: [err.message]
      }, false);
      throw err;
    }
  };

  const hasProduct = product => {
    const prodId = product.id || product.productId;
    return (cart?.items || []).find(item => {
      if (product.promotion?.promotionId) {
        return (
          product.promotion.promotionId === item.promotion?.promotionId
          && prodId === item.productId
        );
      }
      return prodId === item.productId;
    });
  };

  /**
   * Removes product
   * @param product
   * @returns {Promise<void>}
   */
  const removeProduct = async product => {
    const payload = {
      productId: product.id,
      minQuantity: product.minQuantity,
      quantity: 0,
      ...(!product.hasPrice && {
        supplierId: product.supplierId,
        supplierOfferId: product.supplierOfferId,
      })
    };
    if (product.promotion?.promotionId) {
      payload.promotionId = product.promotion.promotionId;
    }
    return handleProducts([payload]);
  };

  /**
   * Updates product qty
   * @param product
   * @returns {Promise<*|null>}
   */
  const updateProduct = async product => {
    const prevProdState = {};
    const productId = (product.id || product.productId);
    setCart(prevCart => {
      if (!('items' in prevCart)) {
        // eslint-disable-next-line no-param-reassign
        prevCart.items = [];
      }
      const idx = prevCart.items.findIndex(item => {
        if (product.promotion?.promotionId) {
          return (
            item.promotion?.promotionId === product.promotion.promotionId
            && item.productId === productId
          );
        }
        return item.productId === productId;
      });
      if (idx !== -1) {
        const prevProd = prevCart.items[idx];
        prevProdState[prevProd.productId || prevProd.id] = {
          ...prevProd
        };
        prevCart.items.reduce((acc, item, i) => {
          if (i === idx) {
            acc.push({
              ...prevProd,
              quantity: Math.min('stockValue' in product ? product.stockValue : 100, product.quantity)
            });
          } else {
            acc.push(item);
          }
          return acc;
        }, []);
      } else {
        // no code means we show loader
        const payload = {
          productId,
          quantity: product.quantity,
          ...(!product.hasPrice && {
            supplierId: product.supplierId,
            supplierOfferId: product.supplierOfferId,
          })
        };
        const promotionId = product.promotionId || product.promotion?.promotionId;
        if (promotionId) {
          payload.promotionId = promotionId;
        }
        prevCart.items.push(payload);
      }
      return { ...prevCart };
    });
    try {
      const payload = {
        productId,
        quantity: product.quantity,
        minQuantity: product.minQuantity,
        ...(!product.hasPrice && {
          supplierId: product.supplierId,
          supplierOfferId: product.supplierOfferId,
        })
      };
      const promotionId = product.promotionId || product.promotion?.promotionId;
      if (promotionId) {
        payload.promotionId = promotionId;
      }
      return await handleProducts([payload]);
    } catch (err) {
      // TODO: handle errors !
      setCart(prevCart => {
        if (!('items' in prevCart)) {
          // eslint-disable-next-line no-param-reassign
          prevCart.items = [];
        }
        const idx = prevCart.items.findIndex(item => {
          if (product.promotion?.promotionId) {
            return (
              item.promotion?.promotionId === product.promotion.promotionId
              && item.productId === productId
            );
          }
          return item.productId === productId;
        });
        if (idx !== -1) {
          const prevProd = prevCart.items[idx];
          if (!prevProd.code) {
            prevCart.items.splice(idx, 1);
          } else {
            prevCart.items.splice(idx, 1, prevProdState[prevProd.productId || prevProd.id]);
          }
        }
        return { ...prevCart };
      });
      throw err;
    }
  };

  let groupedUpdateTimeout = null;
  const groupedUpdatePromises = [];
  const groupedProducts = [];

  const updateProductGrouped = async product => new Promise((resolve, reject) => {
    groupedUpdatePromises.push({ resolve, reject });
    groupedProducts.push(product);

    if (groupedUpdateTimeout) {
      clearTimeout(groupedUpdateTimeout);
    }

    groupedUpdateTimeout = setTimeout(async () => {
      const currentProducts = [...groupedProducts];
      const currentPromises = [...groupedUpdatePromises];

      groupedProducts.length = 0;
      groupedUpdatePromises.length = 0;
      groupedUpdateTimeout = null;

      try {
        const result = await updateProducts(currentProducts);
        currentPromises.forEach(({ resolve: innerResolve }) => innerResolve(result));
      } catch (error) {
        currentPromises.forEach(({ reject: innerReject }) => innerReject(error));
      }
    }, 350);
  });

  /**
   * Updates multiple products at once qty
   * @param {array} products
   * @returns {Promise<*|null>}
   */
  const updateProducts = async products => {
    const prevProdState = {};
    if ((products || []).some(prod => !prod.hasPrice) && userRef.current?.checkout?.dropShipment) {
      return null;
    }
    setCart(prevCart => {
      if (!('items' in prevCart)) {
        // eslint-disable-next-line no-param-reassign
        prevCart.items = [];
      }
      products.forEach(product => {
        const prodId = product.id || product.productId;
        const idx = prevCart.items.findIndex(item => {
          if (product.promotion?.promotionId) {
            return (
              item.promotion?.promotionId === product.promotion.promotionId
              && item.productId === prodId
            );
          }
          return item.productId === prodId;
        });
        if (idx !== -1) {
          const prevProd = prevCart.items[idx];
          prevProdState[prevProd.productId || prevProd.id] = {
            ...prevProd
          };
          prevCart.items.reduce((acc, item, i) => {
            if (i === idx) {
              acc.push({
                ...prevProd,
                quantity: Math.min(
                  'stockValue' in product
                    ? product.stockValue
                    : 100,
                  product.quantity
                )
              });
            } else {
              acc.push(item);
            }
            return acc;
          }, []);
        } else if (!product?.totalPrice) {
          // FIX: cart quick add loading products without info
          // Skip adding this product, as it does not have totalPrice
        } else {
          const payload = {
            productId: product.id,
            quantity: product.quantity,
            minQuantity: product.minQuantity,
            ...(!product.hasPrice && {
              supplierId: product.supplierId,
              supplierOfferId: product.supplierOfferId,
            })
          };
          const promotionId = product.promotionId || product.promotion?.promotionId;
          if (promotionId) {
            payload.promotionId = promotionId;
          }
          prevCart.items.push(payload);
        }
      });
      return { ...prevCart };
    });
    try {
      // eslint-disable-next-line consistent-return
      return await handleProducts(products.map(product => {
        const productId = product.id || product.productId;
        const payload = {
          productId,
          minQuantity: product.minQuantity,
          quantity: product.quantity,
          ...(!product.hasPrice && {
            supplierId: product.supplierId,
            supplierOfferId: product.supplierOfferId,
          })
        };
        const promotionId = product.promotionId || product.promotion?.promotionId;
        if (promotionId) {
          payload.promotionId = promotionId;
        }
        return payload;
      }));
    } catch (err) {
      // TODO: handle errors !
      setCart(prevCart => {
        if (!('items' in prevCart)) {
          // eslint-disable-next-line no-param-reassign
          prevCart.items = [];
        }
        products.forEach(product => {
          const prodId = product.id || product.productId;
          const idx = prevCart.items.findIndex(item => {
            if (product.promotion?.promotionId) {
              return (
                item.promotion?.promotionId === product.promotion.promotionId
                && item.productId === prodId
              );
            }
            return item.productId === prodId;
          });
          if (idx !== -1) {
            const prevProd = prevCart.items[idx];
            if (!prevProd.code) {
              // product did not exist, delete it
              prevCart.items.splice(idx, 1);
            } else {
              prevCart.items.splice(idx, 1, prevProdState[prevProd.productId || prevProd.id]);
            }
          }
        });
        return { ...prevCart };
      });
      throw err;
    }
  };

  const memoizedCart = useMemo(
    () => ({
      data: cart,
      hasSupplierProducts: cart?.items?.some(item => item.supplierId),
      isLoading,
      error: response.error,
      refresh: response.mutate,
      hasProduct,
      removeProduct,
      updateProducts,
      updateProduct: updateProductGrouped,
      updateProductItem: updateProduct,
      emptyCart
    }),
    [cart, response.error, isLoading]
  );

  return (
    <CartContext.Provider value={ memoizedCart }>
      { children }
    </CartContext.Provider>
  );
});

CartProvider.propTypes = {
  children: node
};
CartProvider.displayName = 'CartProvider';

export default CartProvider;
